import { HTMLProps, memo, startTransition, useCallback, useEffect } from 'react';
import { Outlet } from 'react-router-dom';

import useRunOncePerSession from '@/hooks/useRunOncePerSession';
import { useFrontendLogger } from '@/queries/logger/useFrontendLogger';
import { SSEConnectionStatus, useServerSentEvents } from '@/queries/sse/useServerSentEvents';
import { useUpdateStamgegevensInStore } from '@/queries/stamgegevens/useUpdateStamgegevensInStore';
import { RootViewProvider } from '@/router/RootViewContext';
import useVerversAlleWerklijnen from '@/state/selectors/useVerversAlleWerklijnen';
import { useAuth } from '@/utils/auth/context';
import { isNetworkError } from '@/utils/auth/isNetworkError';
import { loginPostedLocalStorageKey } from '@/utils/auth/openidConnectConfig';
import { envVariables } from '@/utils/env';
import ApiConnectionStatus from '@/views/parts/ApiConnectionStatus';

interface RootViewProps extends HTMLProps<HTMLDivElement> {
  sseStateChangedCallback?: (connectionStatus: SSEConnectionStatus) => void;
}
const ConnectionStatus = memo(ApiConnectionStatus);
function RootView(props: Readonly<RootViewProps>) {
  const { sseStateChangedCallback, ...restProps } = props;
  const auth = useAuth();
  const { loginSuccessful, refreshFailed, flushPendingLogs } = useFrontendLogger();
  useRunOncePerSession(loginSuccessful, loginPostedLocalStorageKey, [auth?.isAuthenticated]);

  const { refetchIntervalSecs } = envVariables();

  const { verversAlleWerklijnen } = useVerversAlleWerklijnen();

  // Abonneer op diverse updates v/d clientState via SSE en periodieke updates van stamgegevens:
  const { connectionStatus, sseSessionId, retryCounter } = useServerSentEvents();
  useUpdateStamgegevensInStore();

  useEffect(() => {
    if (sseStateChangedCallback) {
      sseStateChangedCallback(connectionStatus);
    }
  }, [connectionStatus, sseStateChangedCallback]);

  useEffect(() => {
    let refreshInterval;
    if (connectionStatus === 'connected') {
      startTransition(() => {
        verversAlleWerklijnen();
      });
      refreshInterval = setInterval(() => {
        startTransition(() => {
          verversAlleWerklijnen();
        });
      }, 60_000);
    } else {
      refreshInterval = setInterval(
        () => {
          startTransition(() => {
            verversAlleWerklijnen();
          });
        },
        refetchIntervalSecs ? refetchIntervalSecs * 1_000 : 10_000,
      );
    }
    return () => {
      clearInterval(refreshInterval);
    };
  }, [connectionStatus, refetchIntervalSecs, verversAlleWerklijnen]);

  // Met een backend storing, later opnieuw te proberen loggen
  useEffect(() => {
    const interval = setInterval(() => {
      void flushPendingLogs();
    }, 60_000);
    return () => clearInterval(interval);
  }, [flushPendingLogs]);

  const handleRenewFailure = useCallback(
    (error: Error) => {
      refreshFailed(error.message);
      if (isNetworkError(error)) {
        return;
      }
      void auth.logout();
    },
    [auth, refreshFailed],
  );

  useEffect(() => {
    const offExpired = auth?.onTokenExpired?.(() => {
      auth
        .getAccessToken()
        .then((token) => {
          if (!token) {
            handleRenewFailure(new Error('Token is verlopen en kan niet worden vernieuwd.'));
          }
        })
        .catch((err) => {
          handleRenewFailure(err instanceof Error ? err : new Error(String(err)));
        });
    });

    const offRenewError = auth?.onSilentRenewError?.((error) => {
      handleRenewFailure(error);
    });

    return () => {
      offExpired?.();
      offRenewError?.();
    };
  }, [auth, handleRenewFailure]);

  return (
    <>
      <ConnectionStatus isAuthenticated={auth?.isAuthenticated ?? false} sseConnectionStatus={connectionStatus} />
      <RootViewProvider sseConnectionStatus={connectionStatus} sseSessionId={sseSessionId} retryCounter={retryCounter}>
        <div className='root-view' {...restProps}>
          <Outlet />
        </div>
      </RootViewProvider>
    </>
  );
}

export default RootView;
